import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import IconButton from "@mui/material/IconButton";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { bindMenu, bindTrigger } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";
import MuiMenu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import IosShareIcon from "@mui/icons-material/IosShare";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import HistoryIcon from "@mui/icons-material/History";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import PdfReportMenuItem from "../../../components/PdfReportMenuItem";
import useOpenSignedPdfUrl from "../../../hooks/useOpenSignedPdfUrl";
import useRoles from "../../../hooks/user/useRoles";
import ShareModal from "../../../views/inventory/requests/share/ShareModal";
import AttachmentModal from "../../../views/inventory/requests/AttachmentModal";
import OutboundChargesSummaryModal from "../../../views/inventory/requests/orders/OutboundChargesSummaryModal";
import useQuery from "../../../hooks/useQuery";
import InventoryRequestModal from "../../../views/inventory/requests/InventoryRequestModal";
import RevisionLogModal from "../../../components/RevisionLogModal";
import { useQuery as useApolloQuery } from "@apollo/client/react";
import { FETCH_PROJECT_QUERY } from "../../../constants/graphql/queries";

export default function OutboundActions({ item, onRemove, refetch }) {
  const navigate = useNavigate();
  const query = useQuery();
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = React.useState(false);
  const selectedInventoryRequestId = query.get("inventoryRequestId");
  const outboundChargesSummaryId = query.get("outboundChargesSummaryId");
  const openOutboundChargesSummary =
    outboundChargesSummaryId && outboundChargesSummaryId === item.id;
  const inventoryRequestRevisionLogId = query.get("inventoryRequestRevisionLogId");
  const openRevisionLog =
    inventoryRequestRevisionLogId && inventoryRequestRevisionLogId === item.id;
  const isRestrictedRole = useRoles(["employee", "viewer"]);
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const projectId = item?.projectId;
  const popupState = usePopupState({ variant: "popover", popupId: "popup-menu" });

  // Fetch project data to check billing status
  const { data: { project } = {}, loading: projectLoading } = useApolloQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId || !popupState.isOpen,
  });
  const billingEnabled = !!project?.billing;

  const toggleAttachmentModal = () => setOpenAttachmentModal(v => !v);

  const toggleShareModal = () => setOpenShareModal(v => !v);

  const toggleRemoveModal = () => {
    setOpenRemoveModal(v => !v);
  };

  const toggleOutboundChargesSummary = useCallback(() => {
    const newParams = new URLSearchParams(query);
    if (openOutboundChargesSummary) {
      newParams.delete("outboundChargesSummaryId");
    } else {
      newParams.set("outboundChargesSummaryId", item.id);
    }
    navigate({ search: newParams.toString() });
  }, [query, navigate, openOutboundChargesSummary, item.id]);

  const toggleRevisionLog = useCallback(() => {
    const newParams = new URLSearchParams(query);
    if (openRevisionLog) {
      newParams.delete("inventoryRequestRevisionLogId");
    } else {
      newParams.set("inventoryRequestRevisionLogId", item.id);
    }
    navigate({ search: newParams.toString() });
  }, [query, navigate, openRevisionLog, item.id]);

  const openPdfReport = useOpenSignedPdfUrl({
    resourceType: "inventory_request_pdfs",
    resourceId: item.id,
  });
  const openRegeneratedPdfReport = useOpenSignedPdfUrl({
    resourceType: "inventory_request_pdfs",
    resourceId: item.id,
    extraQueryParams: { force_pdf_regeneration: true },
  });

  const toggleEditModal = useCallback(() => {
    const newParams = new URLSearchParams(query);
    if (query.get("inventoryRequestId")) {
      newParams.delete("inventoryRequestId");
      navigate({ search: newParams.toString() });
    } else {
      newParams.set("inventoryRequestId", item.id);
      navigate({ search: newParams.toString() });
    }
  }, [query, navigate]);

  return (
    <>
      {!isRestrictedRole && (
        <Tooltip title="Edit Order">
          <IconButton onClick={() => toggleEditModal()}>
            <EditOutlinedIcon color="action" />
          </IconButton>
        </Tooltip>
      )}
      <React.Fragment>
        <Tooltip title="More Options">
          <IconButton {...bindTrigger(popupState)}>
            <MoreVertIcon color="action" />
          </IconButton>
        </Tooltip>
        <MuiMenu {...bindMenu(popupState)}>
          <PdfReportMenuItem
            onOpen={openPdfReport}
            onRegenerate={openRegeneratedPdfReport}
            onClose={popupState.close}
          />
          <MenuItem
            onClick={() => {
              toggleAttachmentModal();
              popupState.close();
            }}
          >
            <ListItemIcon>
              <AttachFileIcon />
            </ListItemIcon>
            <ListItemText primary="Attachments" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              toggleShareModal();
              popupState.close();
            }}
          >
            <ListItemIcon>
              <IosShareIcon />
            </ListItemIcon>
            <ListItemText primary="Share Order" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              toggleRevisionLog();
              popupState.close();
            }}
          >
            <ListItemIcon>
              <HistoryIcon />
            </ListItemIcon>
            <ListItemText primary="Revision Log" />
          </MenuItem>
          {billingEnabled && isAdminOrManager && (
            <MenuItem
              onClick={() => {
                toggleOutboundChargesSummary();
                popupState.close();
              }}
            >
              <ListItemIcon>
                <RequestQuoteIcon />
              </ListItemIcon>
              <ListItemText primary="Outbound Charges Summary" />
            </MenuItem>
          )}
          {isAdminOrManager && (
            <MenuItem
              onClick={() => {
                toggleRemoveModal();
                popupState.close();
              }}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText primary="Delete Order" />
            </MenuItem>
          )}
        </MuiMenu>
      </React.Fragment>
      {openAttachmentModal && (
        <AttachmentModal inventoryRequestId={item.id} onClose={toggleAttachmentModal} />
      )}
      {openShareModal && <ShareModal inventoryRequest={item} onClose={toggleShareModal} />}
      {openOutboundChargesSummary && (
        <OutboundChargesSummaryModal
          inventoryRequest={item}
          onClose={toggleOutboundChargesSummary}
          isSimpleCheckoutContractWarehousesMode={false}
        />
      )}
      {openRevisionLog && (
        <RevisionLogModal itemId={item.id} itemType="InventoryRequest" onClose={toggleRevisionLog} />
      )}
      {openRemoveModal && (
        <ConfirmDeleteModal
          title="Delete Order"
          descr="Are you sure you want to delete this order?"
          onClose={toggleRemoveModal}
          onDelete={onRemove}
        />
      )}
      {selectedInventoryRequestId === item.id && (
        <InventoryRequestModal id={item.id} onClose={toggleEditModal} reFetch={refetch} />
      )}
    </>
  );
}
